import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { ADMIN_TOKEN_KEY } from "./authApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_SLUG = "davangere-members-day-2026";

/** Normalize a server referral row into the shape the old DemoStore consumers expect. */
function normalize(r) {
  return {
    ...r,
    from: r.from_id,
    to: r.to_id,
    // consumers do `r.value * r.count` for INR totals. Backend already multiplies count in — divide back:
    value: (r.unit_value_paise || 0) / 100,
    ts: r.created_at ? Date.parse(r.created_at) : Date.now(),
    fromMeta: r.from,
    toMeta: r.to,
  };
}

/** Polling hook — used by Table Leader, Big Screen, Admin Dashboard feed. Admin=true uses the
 *  admin endpoint (with Authorization); admin=false hits the public /leader endpoint. */
export function useReferralsLive({ table, round, chapter, limit = 60, intervalMs = 3000, admin = false, enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [loading, setLoading] = useState(true);
  const tick = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const params = { event_slug: EVENT_SLUG, limit };
    if (table) params.table = table;
    if (round) params.round = round;
    if (chapter) params.chapter = chapter;
    const url = admin ? `${API}/admin/referrals` : `${API}/leader/referrals`;
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    const headers = admin && t ? { Authorization: `Bearer ${t}` } : {};
    try {
      const r = await axios.get(url, { params, headers });
      setItems((r.data.items || []).map(normalize));
      setAggregates(r.data.aggregates || null);
    } catch {
      // silent — polling will retry
    } finally { setLoading(false); }
  }, [table, round, chapter, limit, admin, enabled]);

  useEffect(() => {
    tick.current += 1;
    load();
    if (!enabled || intervalMs <= 0) return undefined;
    const iv = setInterval(load, intervalMs);
    return () => clearInterval(iv);
  }, [load, intervalMs, enabled]);

  return { items, aggregates, loading, refresh: load };
}

export async function verifyLeaderPin({ pin, name, phone }) {
  const r = await axios.post(`${API}/leader/pin-verify`, { event_slug: EVENT_SLUG, pin, name, phone: phone || "" });
  return r.data;
}

/** Persist a leader session on the server — so audit says "Ravi signed in at 10:14 from device X" */
export async function startLeaderSession({ name, phone }) {
  const device_hint = (navigator.userAgent || "").slice(0, 100);
  const r = await axios.post(`${API}/leader/session`, {
    event_slug: EVENT_SLUG, name, phone: phone || "", device_hint, pin_used: true,
  });
  return r.data;
}

export async function pingLeaderSession(sid) {
  if (!sid) return;
  try { await axios.post(`${API}/leader/session/${sid}/ping`); } catch {}
}

/** Server-side sign-out — marks the leader_sessions row as ended for the audit trail.
 *  Non-fatal: always resolves so the client can still clear localStorage even if the network fails. */
export async function endLeaderSession(sid) {
  if (!sid) return;
  try { await axios.post(`${API}/leader/session/${sid}/end`); } catch {}
}

export async function postReferral({ round, table, from, to, count, type, value_paise, logged_by_name, logged_by_phone, client_idempotency_key }) {
  const body = { event_slug: EVENT_SLUG, round, table, from, to, count, type,
    ...(value_paise != null ? { value_paise } : {}),
    ...(logged_by_name ? { logged_by_name } : {}),
    ...(logged_by_phone ? { logged_by_phone } : {}),
    ...(client_idempotency_key ? { client_idempotency_key } : {}),
  };
  const r = await axios.post(`${API}/leader/referrals`, body);
  return r.data;
}

export async function deleteReferral(id, logged_by_name) {
  const params = logged_by_name ? { logged_by_name } : {};
  const r = await axios.delete(`${API}/leader/referrals/${id}`, { params });
  return r.data;
}

export async function promoteReferral(referralId, { leader_name, leader_phone, ttl_seconds = 45 } = {}) {
  const r = await axios.post(`${API}/leader/referrals/${referralId}/promote`, {
    event_slug: EVENT_SLUG,
    ...(leader_name ? { leader_name } : {}),
    ...(leader_phone ? { leader_phone } : {}),
    ttl_seconds,
  });
  return r.data;
}

export function newIdempotencyKey() {
  // Prefer native UUID; fall back to a Math.random-ish string for old browsers
  try { return crypto.randomUUID(); } catch { return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
}

export async function fetchBigScreenSpotlight() {
  const r = await axios.get(`${API}/leader/bigscreen/state`, { params: { event_slug: EVENT_SLUG } });
  return r.data.spotlight;
}

export async function setBigScreenSpotlight(table, note) {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  const r = await axios.post(`${API}/admin/bigscreen/spotlight`,
    { event_slug: EVENT_SLUG, table, note },
    { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  return r.data.spotlight;
}
